import {
    CloseOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    ExportOutlined,
    QuestionCircleFilled,
} from '@ant-design/icons';
import {
    Button,
    Card,
    Col,
    DatePicker,
    Divider,
    Flex,
    Form,
    Image,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {ColumnsType} from 'antd/lib/table';
import dayjs from 'dayjs';
import React, {useEffect} from 'react';
import {Link, useNavigate, useParams} from 'react-router-dom';
import {v4 as uuidv4} from 'uuid';

import {ApiQuery} from '../../../gen/api';
import {useAdminGroupSaleDetailQuery, useAdminGroupSaleUpdateMutation} from '../../../gen/api/apis';
import {
    AdminGroupSaleDetail,
    AdminGroupSaleListProduct,
    AdminMarketplace,
    AdminProductListImage,
    AdminProductStatus,
    ProductConsistency,
} from '../../../gen/api/models';
import {useNotification} from '../../../NotificationContext';
import {groupSaleStatusTypes, marketplaceLabels} from '../../helpers/labels';
import {groupSaleStatusBadge, groupSaleStatusLabel, productStatusBadge, productStatusLabel} from '../../helpers/status';

const {confirm} = Modal;

export const GroupSaleDetail = () => {
    useEffect(() => {
        const handleKeyDown = (event: {key: string}) => {
            if (event.key === 'Escape') {
                navigate('/group-sales');
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        // Cleanup the event listener on unmount
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const notify = useNotification();
    const navigate = useNavigate();

    const {id} = useParams();
    const [form] = Form.useForm<AdminGroupSaleDetail>();
    const [products, setProducts] = React.useState<AdminGroupSaleListProduct[] | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    const fetchGroupSaleDetails = useAdminGroupSaleDetailQuery(
        {
            id: id || '',
        },
        {
            refetchOnMount: 'always',
            onSuccess: (data) => {
                form.setFieldsValue(data);
                setProducts(data.products || []);
            },
            onError: (error) => {
                console.log(error);
            },
        }
    );

    const categoryQuery = ApiQuery.useGetAdminProductCategoryListQuery({
        refetchOnMount: 'always',
    });

    const palletOrdersQuery = ApiQuery.useAdminGetAllPalletsQuery({
        refetchOnMount: 'always',
    });

    const productConsistencyDetails = ApiQuery.useGetAdminProductsConsistencyDetailsQuery({
        refetchOnMount: 'always',
    });

    const updateQuery = useAdminGroupSaleUpdateMutation({
        onSuccess: () => {
            notify.openNotificationWithIcon('success', 'Group Sale updated successfully');
        },
        onError: (error) => {
            console.error(error);
            notify.openNotificationWithIcon('error', 'Error updating Group Sale');
        },
    });

    const handleSubmit = () => {
        console.log(form.getFieldsValue());
        console.log();
        form.validateFields()
            .then((values: AdminGroupSaleDetail) => {
                if (!id) {
                    notify.openNotificationWithIcon('error', 'Group Sale ID is missing');
                    return;
                }
                updateQuery.mutate({
                    id: id,
                    params: {
                        id: id,
                        name: values.name,
                        status: values.status,
                        soldPrice: values.soldPrice,
                        soldDate: values.soldDate,
                        marketplaces:
                            values.marketplaces?.map((mp: any) => {
                                return {
                                    id: mp.id || uuidv4(),
                                    type: mp.type,
                                    link: mp.link,
                                    price: mp.price,
                                    updatedAt: dayjs().toISOString(),
                                    createdAt: dayjs().toISOString(),
                                };
                            }) || [],
                        productIDs: products?.map((p) => p.id) || [],
                    },
                });
                // navigate('/group-sales');
            })
            .catch((err) => {
                console.error(err);
                setError('Please fill all required fields');
            });
    };

    const productColumns: ColumnsType<AdminGroupSaleListProduct> = [
        {
            dataIndex: 'images',
            key: 'images',

            render: (images: Array<AdminProductListImage>, product) => (
                <Flex
                    align={'center'}
                    justify={'space-between'}
                    gap={10}
                >
                    <Button
                        variant={'dashed'}
                        icon={<DeleteOutlined style={{color: 'red'}} />}
                        size={'small'}
                        onClick={() => {
                            // Remove product from group sale
                            setProducts(products?.filter((p) => p.id !== product.id) || []);
                        }}
                    />
                    {images?.length && (
                        <Image
                            width={25}
                            preview={false}
                            src={images?.find((img) => img.isPrimary)?.thumbnailPath}
                        />
                    )}
                </Flex>
            ),
        },
        {
            title: (
                <Flex
                    justify={'space-between'}
                    align={'center'}
                >
                    <span>ID</span>
                </Flex>
            ),
            dataIndex: 'listingNumber',
            key: 'id',
            minWidth: 120,

            render: (id, product) => {
                const extra = product.asin && (
                    <a>
                        <Tooltip title={`Find Product: ${product.asin}`}>
                            <Button
                                size={'small'}
                                type="link"
                                onClick={() => window.open(`https://www.google.com/search?q=${product.asin}`, '_blank')}
                                icon={<ExportOutlined />}
                                iconPosition={'end'}
                            >
                                {` / ASIN`}
                            </Button>
                        </Tooltip>
                    </a>
                );
                return (
                    <Flex
                        align={'center'}
                        gap={5}
                    >
                        <Link to={`/products/${product.id}`}>{id}</Link>
                        {extra}
                    </Flex>
                );
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text: string) => (
                <Flex
                    align={'center'}
                    justify={'space-between'}
                >
                    <div style={{maxWidth: 300, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                        {text}
                    </div>
                </Flex>
            ),
        },
        {
            title: (
                <>
                    PP{' '}
                    <Tooltip
                        placement="right"
                        title="Purchace Price"
                    >
                        <QuestionCircleFilled />
                    </Tooltip>
                </>
            ),
            dataIndex: 'purchasePrice',
            key: 'purchasePrice',
            render: (price) => <>{price != 0 ? price : 'N/A'}</>,
        },
        {
            title: 'MSRP',
            dataIndex: 'msrp',
            key: 'msrp',
            render: (price) => <>${price}</>,
        },
        {
            title: (
                <>
                    LRP{' '}
                    <Tooltip
                        placement="right"
                        title="Lowest Resale Price"
                    >
                        <QuestionCircleFilled />
                    </Tooltip>
                </>
            ),
            dataIndex: 'lowestResalePrice',
            key: 'lowestResalePrice',
            render: (price) => <>${price}</>,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',

            render: (status: AdminProductStatus) => {
                return <Tag color={productStatusBadge(status)}>{productStatusLabel(status)}</Tag>;
            },
        },
        {
            title: 'Consistency',
            dataIndex: 'consistency',
            key: 'consistency',

            render: (consistency: ProductConsistency) => {
                const color = consistency === ProductConsistency.ProductConsistencyNew ? 'green' : 'orange';
                return <Tag color={color}>{consistency.toUpperCase()}</Tag>;
            },
        },
        {
            title: 'Consistency Details',
            dataIndex: 'consistencyDetails',
            key: 'consistencyDetails',

            render: (consistency: string[]) => {
                return consistency?.map((c) => <Tag key={c}>{c}</Tag>);
            },
        },
        {
            title: 'Marketplaces',
            dataIndex: 'marketplaces',
            key: 'marketplaces',
            render: (marketplaces: Array<AdminMarketplace>) => {
                if (marketplaces?.length === 4) {
                    return <Tag color={'green'}>ALL</Tag>;
                } else {
                    return (
                        <Flex>
                            {marketplaces?.map((marketplace) => <Tag key={marketplace.id}>{marketplace.name}</Tag>)}
                        </Flex>
                    );
                }
            },
        },
        {
            title: 'Pallet Order',
            dataIndex: 'palletOrderID',
            key: 'palletOrderIDs',
            render: (id) => {
                const pallet = palletOrdersQuery.data?.items?.find((p) => p.id === id);
                if (pallet) {
                    return (
                        <a onClick={() => navigate(`/pallet-orders/${id}`)}>
                            {pallet?.name} - {dayjs(pallet?.orderDate).format('MM/DD/YYYY')}
                        </a>
                    );
                }
            },
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => dayjs(date).format('MM/DD/YYYY hh:mm A'),
        },
    ];

    if (fetchGroupSaleDetails.isError) {
        return (
            <div>
                <Typography.Text type="danger">Not found</Typography.Text>
            </div>
        );
    }

    if (fetchGroupSaleDetails.isLoading) {
        return (
            <Flex
                justify={'center'}
                align={'center'}
                style={{height: '100%'}}
            >
                <Typography.Text>Loading...</Typography.Text>
            </Flex>
        );
    }

    return (
        <>
            <Flex
                justify="space-between"
                align={'center'}
                style={{marginBottom: '1rem'}}
            >
                <Flex align={'center'}>
                    <Typography.Title
                        level={3}
                        style={{margin: '0 10px 0 0'}}
                    >
                        {fetchGroupSaleDetails.data?.name}
                    </Typography.Title>
                </Flex>
            </Flex>
            <Form
                layout="horizontal"
                labelCol={{span: 5}}
                wrapperCol={{span: 19, flex: 1}}
                colon={false}
                form={form}
                labelWrap
            >
                <Row gutter={16}>
                    <Col flex={'60%'}>
                        <Card
                            title={
                                <Flex
                                    align={'center'}
                                    justify={'space-between'}
                                >
                                    <h2>Product Details</h2>
                                    <Tag color={groupSaleStatusBadge(fetchGroupSaleDetails.data!.status)}>
                                        {groupSaleStatusLabel(fetchGroupSaleDetails.data!.status)}
                                    </Tag>
                                </Flex>
                            }
                        >
                            <Form.Item
                                label="Name"
                                name="name"
                                rules={[{required: true, message: 'Name is required'}]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                label="Status"
                                name="status"
                                rules={[{required: true, message: 'Status is required'}]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="label"
                                    placeholder="select status"
                                    options={groupSaleStatusTypes.map((m) => ({
                                        label: m.label,
                                        value: m.key,
                                    }))}
                                />
                            </Form.Item>
                            <Form.Item label="Sold Details">
                                <Space.Compact>
                                    <Form.Item name="sold_price">
                                        <InputNumber
                                            prefix="$"
                                            min={0}
                                            max={100000}
                                            defaultValue={0}
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        name="sold_date"
                                        initialValue={dayjs()}
                                    >
                                        <DatePicker
                                            placeholder={'Date'}
                                            showTime
                                        />
                                    </Form.Item>
                                </Space.Compact>
                            </Form.Item>
                        </Card>
                    </Col>
                    <Col flex={'40%'}>
                        <Form.List name="marketplaces">
                            {(fields, {add, remove}) => (
                                <div style={{display: 'flex', rowGap: 16, flexDirection: 'column'}}>
                                    {fields.map((field) => (
                                        <Card
                                            size="small"
                                            key={field.key}
                                            extra={
                                                <CloseOutlined
                                                    onClick={() => {
                                                        confirm({
                                                            title: 'Are you sure you want to delete this marketplace?',
                                                            icon: <ExclamationCircleFilled />,
                                                            content: (
                                                                <>
                                                                    <p>
                                                                        To confirm the deletion, you will need to update
                                                                        the product when you are done.
                                                                    </p>
                                                                    <p>
                                                                        Orders associated with this marketplace will not
                                                                        be deleted.
                                                                    </p>
                                                                </>
                                                            ),

                                                            okText: 'Yes',
                                                            okType: 'danger',
                                                            cancelText: 'No',
                                                            onOk() {
                                                                remove(field.name);
                                                            },
                                                            onCancel() {
                                                                console.log('Cancel');
                                                            },
                                                        });
                                                    }}
                                                />
                                            }
                                        >
                                            <Form.Item
                                                label="Marketplace"
                                                name={[field.name, 'type']}
                                            >
                                                <Select
                                                    showSearch
                                                    optionFilterProp="label"
                                                    placeholder="select marketplace"
                                                    options={marketplaceLabels.map((m) => ({
                                                        label: m.label,
                                                        value: m.key,
                                                    }))}
                                                />
                                            </Form.Item>
                                            <Form.Item
                                                label="Link"
                                                name={[field.name, 'link']}
                                            >
                                                <Input />
                                            </Form.Item>
                                            <Form.Item
                                                label="Price"
                                                name={[field.name, 'price']}
                                            >
                                                <InputNumber
                                                    min={0}
                                                    max={100000}
                                                />
                                            </Form.Item>
                                        </Card>
                                    ))}

                                    <Button
                                        style={{marginBottom: 16}}
                                        type="dashed"
                                        onClick={() => add()}
                                        block
                                    >
                                        + Add Marketplace
                                    </Button>
                                </div>
                            )}
                        </Form.List>
                    </Col>
                </Row>

                <Typography.Title level={4}>Assigned products</Typography.Title>
                <Table<AdminGroupSaleListProduct>
                    rowKey={(record) => record.id}
                    columns={productColumns}
                    key={'id'}
                    dataSource={products || []}
                    loading={fetchGroupSaleDetails.isLoading}
                    scroll={{x: 'max-content'}}
                    size={'small'}
                    pagination={false}
                />
            </Form>
            <Divider />
            <Flex
                justify={'end'}
                align={'center'}
                gap={16}
            >
                <Button
                    type="default"
                    onClick={() => navigate('/group-sales')}
                >
                    Cancel
                </Button>
                <Flex justify={'end'}>
                    <Button
                        type="primary"
                        htmlType="submit"
                        // loading={updateQuery.isLoading}
                        onClick={handleSubmit}
                    >
                        Update
                    </Button>
                </Flex>
            </Flex>
        </>
    );
};
